import {Injectable, signal, computed, effect, DestroyRef, inject, untracked} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Client} from "../models/client.model";
import {catchError, tap} from "rxjs/operators";
import {DatevUserInfo} from "../models/user-info.model";
import {AuthStatusService} from "./auth-status.service";
import {NotificationService} from "./notification.service";
import {ApiAvailability} from "../models/api-availability.model";

@Injectable({
    providedIn: 'root'
})
export class DatevAuthStatusService {
    private baseUrl = `${environment.apiUrl}/datev`;
    private datevAuthStatus = signal<boolean>(false);
    private datevUserInfo = signal<DatevUserInfo | null>(null);
    private userInfoLoading = signal<boolean>(false);
    private navigationEnabled = signal<boolean>(false);
    private apiAvailabilities = signal<ApiAvailability[]>([]);
    private allApisAvailable = signal<boolean>(true);
    private intervalId: any;
    private destroyRef = inject(DestroyRef);

    constructor(private http: HttpClient, private authService: AuthStatusService, private notificationService: NotificationService) {
        effect(() => {
            if (this.authService.getStatus()()) {
                this.startPolling();
            } else {
                this.stopPolling();
            }
        });
        effect(() => {
            if (this.datevAuthStatus()) {
                untracked(() => {
                    try {
                        this.fetchDatevUserInfo();
                        this.fetchApiAvailability();
                    } catch (error) {
                        console.log(">>>>>" + error);
                    }
                })
            } else {
                untracked(() => {
                    this.apiAvailabilities.set([]);
                    this.allApisAvailable.set(true);
                });
            }
        });
        this.destroyRef.onDestroy(() => {
            this.stopPolling();
        });
    }


    getUserInfo() {
        return this.datevUserInfo.asReadonly();
    }

    getAuthStatus() {
        return this.datevAuthStatus.asReadonly();
    }

    getNavigationEnabled() {
        return this.navigationEnabled.asReadonly();
    }

    getUserInfoLoading() {
        return this.userInfoLoading.asReadonly();
    }

    getAllApisAvailable() {
        return this.allApisAvailable.asReadonly();
    }

    getApiAvailabilities() {
        return this.apiAvailabilities.asReadonly();
    }

    private startPolling() {
        this.stopPolling();
        this.checkStatus();

        // Poll every 5 seconds
        this.intervalId = setInterval(() => {
            this.checkStatus();
        }, 5000);
    }

    private stopPolling() {
        clearInterval(this.intervalId);
    }

    private fetchDatevUserInfo() {
        this.userInfoLoading.set(true);
        this.http.get<DatevUserInfo>(`${this.baseUrl}/user-info`)
            .subscribe({
                next: userInfo => {
                    console.log("got user info", userInfo);
                    this.datevUserInfo.set(userInfo);
                    this.userInfoLoading.set(false);
                },
                error: error => {
                    console.error("Error fetching user info:", error);
                    this.userInfoLoading.set(false);
                }
            });
    }

    private checkStatus() {
        fetch(this.baseUrl + "/is-authenticated").then(response => {
            if (response.ok) {
                this.datevAuthStatus.set(true);
            } else {
                this.datevAuthStatus.set(false);
                this.datevUserInfo.set(null);
            }
        }).catch(error => {
            this.datevAuthStatus.set(false);
            this.datevUserInfo.set(null);
        })
    }

    private fetchApiAvailability() {
        this.http.get<ApiAvailability[]>(`${this.baseUrl}/available-apis`)
            .subscribe({
                next: (apis) => {
                    this.apiAvailabilities.set(apis);
                    this.allApisAvailable.set(apis.every(api => api.accessable));
                },
                error: () => {
                    this.apiAvailabilities.set([]);
                    this.allApisAvailable.set(false);
                }
            });
    }

    loginDatev() {
        window.open(`${this.baseUrl}/login`, "_self")
    }

    logoutDatev() {
        this.http.get(`${this.baseUrl}/logout`).subscribe({
            next: () => {
                console.log("logout successful");
            },
            error: (error) => {
                console.error("logout failed ", error)
            }
        })
    }
}

export class User {
    public username: string = "";
    public consultant_id: string = "";
    public client_id: string = "";
}